import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { motion } from 'framer-motion';
import { Search, QrCode, Shield, Truck, Leaf, ChevronRight, AlertCircle } from 'lucide-react';
import { getExampleCode } from '../lib/traceabilite/provider';

const Tracabilite = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = language === 'fr';
  
  const [searchCode, setSearchCode] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState('');

  const exampleCode = getExampleCode();

  const handleSearch = async (e) => {
    e.preventDefault();
    
    if (!searchCode.trim()) {
      setError(t ? 'Veuillez entrer un code de lot' : 'Please enter a lot code');
      return;
    }

    setIsSearching(true);
    setError('');

    // Normaliser le code (majuscules, sans espaces)
    const normalizedCode = searchCode.trim().toUpperCase();
    
    // Naviguer vers la page de détails
    navigate(`/tracabilite/${normalizedCode}`);
  };

  const handleExampleClick = () => {
    setSearchCode(exampleCode);
    setError('');
  };

  const features = [
    {
      icon: Shield,
      title: t ? 'Origine certifiée' : 'Certified Origin',
      description: t ? 'Vérifiez l\'authenticité et la provenance de vos produits' : 'Verify the authenticity and origin of your products'
    },
    {
      icon: Truck,
      title: t ? 'Parcours complet' : 'Complete Journey',
      description: t ? 'Suivez chaque étape du producteur jusqu\'à votre rayon' : 'Track every step from producer to your shelf'
    },
    {
      icon: Leaf,
      title: t ? 'Certifications' : 'Certifications',
      description: t ? 'Consultez les labels qualité et environnementaux' : 'View quality and environmental labels'
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8F2E8]">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#1A5C38] via-[#1A5C38] to-[#1A9070] overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.03%22%3E%3Cpath%20d%3D%22M36%2034v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6%2034v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6%204V0H4v4H0v2h4v4h2V6h4V4H6z%22%2F%3E%3C%2Fg%3E%3C%2Fg%3E%3C%2Fsvg%3E')] opacity-50" />
        
        <div className="container relative z-10">
          <div className="max-w-3xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center justify-center gap-3 mb-6"
            >
              <div className="w-12 h-12 bg-[#F5A623] rounded-xl flex items-center justify-center">
                <QrCode className="text-[#1A5C38]" size={28} />
              </div>
              <span className="text-[#F5A623] font-semibold tracking-wider uppercase text-sm">
                SmartTrace
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold text-white mb-6"
              style={{ fontFamily: 'Syne, sans-serif' }}
            >
              {t ? 'Traçabilité de vos produits' : 'Product Traceability'}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-xl text-white/80 mb-10"
            >
              {t 
                ? 'Scannez le QR code ou entrez le code de lot pour découvrir l\'origine et le parcours de vos fruits et légumes.'
                : 'Scan the QR code or enter the lot code to discover the origin and journey of your fruits and vegetables.'}
            </motion.p>

            {/* Search Form */}
            <motion.form
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              onSubmit={handleSearch}
              className="max-w-xl mx-auto"
            >
              <div className="relative">
                <input
                  type="text"
                  value={searchCode}
                  onChange={(e) => {
                    setSearchCode(e.target.value.toUpperCase());
                    setError('');
                  }}
                  placeholder={t ? 'Entrez le code du lot (ex: OCN-MK-2024-001)' : 'Enter lot code (e.g., OCN-MK-2024-001)'}
                  className="w-full px-6 py-4 pr-14 text-lg rounded-full border-2 border-white/20 bg-white/10 text-white placeholder-white/50 focus:outline-none focus:border-[#F5A623] focus:bg-white/20 transition-all"
                  data-testid="trace-search-input"
                />
                <button
                  type="submit"
                  disabled={isSearching}
                  className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#F5A623] hover:bg-[#E09000] rounded-full flex items-center justify-center transition-colors disabled:opacity-50"
                  data-testid="trace-search-button"
                >
                  <Search className="text-[#1A5C38]" size={20} />
                </button>
              </div>

              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-center gap-2 mt-3 text-red-300"
                >
                  <AlertCircle size={16} />
                  <span className="text-sm">{error}</span>
                </motion.div>
              )}

              <p className="text-white/60 text-sm mt-4">
                {t ? 'Exemple : ' : 'Example: '}
                <button
                  type="button"
                  onClick={handleExampleClick}
                  className="text-[#F5A623] hover:underline"
                  data-testid="trace-example-link"
                >
                  {exampleCode}
                </button>
              </p>
            </motion.form>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A5C38] mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              {t ? 'Transparence totale' : 'Total Transparency'}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              {t 
                ? 'Océana vous garantit une traçabilité complète de vos produits, du producteur au rayon.'
                : 'Océana guarantees complete traceability of your products, from producer to shelf.'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center p-8 bg-[#F8F2E8] rounded-2xl"
              >
                <div className="w-16 h-16 bg-[#1A5C38] rounded-xl flex items-center justify-center mx-auto mb-6">
                  <feature.icon className="text-white" size={32} />
                </div>
                <h3 className="text-xl font-bold text-[#1A5C38] mb-3" style={{ fontFamily: 'Syne, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-[#F8F2E8]">
        <div className="container">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1A5C38] mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              {t ? 'Comment ça marche ?' : 'How does it work?'}
            </h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: '1',
                  title: t ? 'Scannez ou entrez le code' : 'Scan or enter the code',
                  description: t 
                    ? 'Utilisez l\'appareil photo de votre téléphone pour scanner le QR code sur l\'emballage, ou entrez le code manuellement.'
                    : 'Use your phone camera to scan the QR code on the packaging, or enter the code manually.'
                },
                {
                  step: '2',
                  title: t ? 'Consultez l\'origine' : 'View the origin',
                  description: t
                    ? 'Découvrez d\'où vient votre produit : pays, région, producteur et date de récolte.'
                    : 'Discover where your product comes from: country, region, producer, and harvest date.'
                },
                {
                  step: '3',
                  title: t ? 'Suivez le parcours' : 'Track the journey',
                  description: t
                    ? 'Visualisez chaque étape du transport, avec les conditions de conservation.'
                    : 'View each step of transport, with storage conditions.'
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="relative"
                >
                  <div className="text-center">
                    <div className="w-14 h-14 bg-[#1A5C38] rounded-full flex items-center justify-center mx-auto mb-4 text-white text-2xl font-bold">
                      {item.step}
                    </div>
                    <h3 className="text-lg font-bold text-[#1A5C38] mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
                      {item.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {item.description}
                    </p>
                  </div>
                  {index < 2 && (
                    <div className="hidden md:block absolute top-7 left-[60%] w-[80%]">
                      <ChevronRight className="text-[#1A5C38]/30" size={24} />
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#1A5C38]">
        <div className="container text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
            {t ? 'Vous êtes distributeur ?' : 'Are you a distributor?'}
          </h2>
          <p className="text-white/80 mb-8 max-w-xl mx-auto">
            {t 
              ? 'Intégrez SmartTrace dans votre chaîne d\'approvisionnement et offrez la transparence à vos clients.'
              : 'Integrate SmartTrace into your supply chain and offer transparency to your customers.'}
          </p>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-[#F5A623] hover:bg-[#E09000] text-[#1A5C38] font-semibold px-8 py-4 rounded-full transition-all"
          >
            {t ? 'Nous contacter' : 'Contact us'}
            <ChevronRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default Tracabilite;
